import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import React, { useEffect, useState, useMemo } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs, getDoc, doc } from "firebase/firestore";
import Header from "../components/Header";
import { useStateContext } from "../lib/context";
import { Ionicons } from "@expo/vector-icons";

const STATUS_FILTERS = ["Todos", "Procesando", "Pendiente", "Finalizado"];

const SORT_OPTIONS = [
  { key: "date_desc", label: "Más reciente" },
  { key: "date_asc",  label: "Más antiguo"  },
  { key: "total_desc", label: "Mayor total"  },
  { key: "total_asc",  label: "Menor total"  },
];

const OrdersViajantes = ({ navigation }) => {
  const [operaciones, setOperaciones]   = useState([]);
  const [clientNames, setClientNames]   = useState({});
  const [loading, setLoading]           = useState(true);
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [sortKey, setSortKey]           = useState("date_desc");
  const [sortOpen, setSortOpen]         = useState(false);

  const { correoViajante } = useStateContext();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const q = query(
          collection(db, "orders"),
          where("viajante", "==", correoViajante)
        );
        const snap = await getDocs(q);
        const fetched = [];
        snap.forEach((d) => fetched.push(d.data()));
        setOperaciones(fetched);

        // Fetch client names for all unique users
        const uniqueUsers = [...new Set(fetched.map((o) => o.user).filter(Boolean))];
        const nameMap = {};
        await Promise.all(
          uniqueUsers.map(async (email) => {
            try {
              const userSnap = await getDoc(doc(db, "users", email));
              if (userSnap.exists()) {
                nameMap[email] = userSnap.data()?.data?.APELLIDO ?? null;
              }
            } catch {
              // leave null
            }
          })
        );
        setClientNames(nameMap);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [correoViajante]);

  const displayed = useMemo(() => {
    let list = statusFilter === "Todos"
      ? operaciones
      : operaciones.filter((o) => (o.estado || "Procesando") === statusFilter);

    list = [...list].sort((a, b) => {
      if (sortKey === "date_desc") return new Date(b.fecha) - new Date(a.fecha);
      if (sortKey === "date_asc")  return new Date(a.fecha) - new Date(b.fecha);
      if (sortKey === "total_desc") return Number(b.total) - Number(a.total);
      if (sortKey === "total_asc")  return Number(a.total) - Number(b.total);
      return 0;
    });

    return list;
  }, [operaciones, statusFilter, sortKey]);

  const currentSortLabel = SORT_OPTIONS.find((o) => o.key === sortKey)?.label;

  if (loading) {
    return (
      <View style={s.loadingContainer}>
        <ActivityIndicator size="large" color="#080680" />
        <Text style={s.loadingText}>Cargando pedidos...</Text>
      </View>
    );
  }

  return (
    <View style={s.container}>
      <Header title="Mis Pedidos" navigation={navigation} isBack={true} />

      {/* Status filter chips */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={s.filterBar}
        contentContainerStyle={s.filterBarContent}
      >
        {STATUS_FILTERS.map((f) => (
          <TouchableOpacity
            key={f}
            style={[s.chip, statusFilter === f && s.chipActive]}
            onPress={() => setStatusFilter(f)}
            activeOpacity={0.7}
          >
            <Text style={[s.chipText, statusFilter === f && s.chipTextActive]}>{f}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Sort + count bar */}
      <View style={s.sortBar}>
        <Text style={s.resultCount}>
          {displayed.length} pedido{displayed.length !== 1 ? "s" : ""}
        </Text>
        <TouchableOpacity
          style={s.sortBtn}
          onPress={() => setSortOpen((v) => !v)}
          activeOpacity={0.7}
        >
          <Ionicons name="swap-vertical-outline" size={15} color="#080680" />
          <Text style={s.sortBtnText}>{currentSortLabel}</Text>
          <Ionicons
            name={sortOpen ? "chevron-up" : "chevron-down"}
            size={13}
            color="#080680"
          />
        </TouchableOpacity>
      </View>

      {/* Sort dropdown */}
      {sortOpen && (
        <View style={s.sortDropdown}>
          {SORT_OPTIONS.map((opt) => (
            <TouchableOpacity
              key={opt.key}
              style={s.sortOption}
              onPress={() => { setSortKey(opt.key); setSortOpen(false); }}
            >
              <Text style={[s.sortOptionText, sortKey === opt.key && s.sortOptionActive]}>
                {opt.label}
              </Text>
              {sortKey === opt.key && (
                <Ionicons name="checkmark" size={15} color="#080680" />
              )}
            </TouchableOpacity>
          ))}
        </View>
      )}

      <ScrollView
        style={s.scrollView}
        contentContainerStyle={s.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {displayed.length === 0 ? (
          <View style={s.emptyContainer}>
            <Ionicons name="receipt-outline" size={64} color="#ddd" />
            <Text style={s.emptyText}>
              {statusFilter !== "Todos"
                ? `No hay pedidos con estado "${statusFilter}".`
                : "No hay pedidos realizados aún."}
            </Text>
            {statusFilter !== "Todos" && (
              <TouchableOpacity onPress={() => setStatusFilter("Todos")}>
                <Text style={s.clearFilter}>Ver todos</Text>
              </TouchableOpacity>
            )}
          </View>
        ) : (
          displayed.map((item, index) => {
            const dateObj = new Date(item.fecha);
            const formattedDate = dateObj.toLocaleDateString("es-AR", {
              day: "2-digit", month: "2-digit", year: "numeric",
            });
            const formattedTime = dateObj.toLocaleTimeString("es-AR", {
              hour: "2-digit", minute: "2-digit",
            });
            const clientCuit    = item.user?.replace("@mail.com", "") ?? "—";
            const clientName    = clientNames[item.user] ?? null;
            const estado        = item.estado || "Procesando";
            const isFinalizado  = estado === "Finalizado";
            const isPendiente   = estado === "Pendiente";

            return (
              <TouchableOpacity
                key={index}
                style={s.card}
                onPress={() => navigation.navigate("Order", item)}
                activeOpacity={0.8}
              >
                {/* Card header: client + status */}
                <View style={s.cardHeader}>
                  <View style={s.clientBlock}>
                    <Text style={s.clientName}>{clientName ?? clientCuit}</Text>
                    {clientName && <Text style={s.clientCuit}>CUIT {clientCuit}</Text>}
                  </View>
                  <View style={[
                    s.statusBadge,
                    isFinalizado ? s.statusFinalizado
                      : isPendiente ? s.statusPendiente
                      : s.statusProcesando,
                  ]}>
                    <Text style={[
                      s.statusText,
                      isFinalizado ? s.statusTextFinalizado
                        : isPendiente ? s.statusTextPendiente
                        : s.statusTextProcesando,
                    ]}>
                      {estado}
                    </Text>
                  </View>
                </View>

                {/* Divider */}
                <View style={s.divider} />

                {/* Details */}
                <View style={s.cardContent}>
                  <View style={s.row}>
                    <View style={s.dateContainer}>
                      <Ionicons name="calendar-outline" size={13} color="#999" />
                      <Text style={s.dateText}>{formattedDate} · {formattedTime}</Text>
                    </View>
                    <Text style={s.totalValue}>${Number(item.total).toFixed(2)}</Text>
                  </View>

                  <View style={s.tagsRow}>
                    {item.ero && Number(item.ero) > 0 && (
                      <View style={s.tag}>
                        <Text style={s.tagText}>ERO ${Number(item.ero).toFixed(0)}</Text>
                      </View>
                    )}
                    {item.drn && Number(item.drn) > 0 && (
                      <View style={[s.tag, s.tagDrn]}>
                        <Text style={[s.tagText, s.tagTextDrn]}>DRN ${Number(item.drn).toFixed(0)}</Text>
                      </View>
                    )}
                    {Array.isArray(item.items) && (
                      <View style={s.tagNeutral}>
                        <Text style={s.tagTextNeutral}>
                          {item.items.length} ítem{item.items.length !== 1 ? "s" : ""}
                        </Text>
                      </View>
                    )}
                    {item.discount != "0" && Number(item.discount) > 0 && (
                      <View style={s.tagDiscount}>
                        <Text style={s.tagTextDiscount}>
                          -${Number(item.discount).toFixed(0)} dto.
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              </TouchableOpacity>
            );
          })
        )}
        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
};

export default OrdersViajantes;

const s = StyleSheet.create({
  container:       { flex: 1, backgroundColor: "#F4F6FD" },
  loadingContainer:{ flex: 1, backgroundColor: "#F4F6FD", justifyContent: "center", alignItems: "center" },
  loadingText:     { marginTop: 10, color: "#666" },

  // Filter bar
  filterBar:        { maxHeight: 52, backgroundColor: "#fff", borderBottomWidth: 1, borderBottomColor: "#eee" },
  filterBarContent: { paddingHorizontal: 16, paddingVertical: 10, gap: 8, flexDirection: "row" },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 5,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#ddd",
    backgroundColor: "#fff",
  },
  chipActive:     { backgroundColor: "#080680", borderColor: "#080680" },
  chipText:       { fontSize: 13, color: "#555", fontWeight: "500" },
  chipTextActive: { color: "#fff" },

  // Sort bar
  sortBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  resultCount: { fontSize: 13, color: "#aaa" },
  sortBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "#EEF0FB",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  sortBtnText: { fontSize: 13, color: "#080680", fontWeight: "600" },

  // Sort dropdown
  sortDropdown: {
    marginHorizontal: 16,
    backgroundColor: "#fff",
    borderRadius: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 4,
    marginBottom: 4,
  },
  sortOption: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f5f5f5",
  },
  sortOptionText:   { fontSize: 14, color: "#333" },
  sortOptionActive: { color: "#080680", fontWeight: "700" },

  // List
  scrollView:   { flex: 1 },
  scrollContent:{ paddingHorizontal: 16, paddingTop: 4 },
  emptyContainer: { alignItems: "center", marginTop: 60 },
  emptyText:    { marginTop: 16, color: "#999", fontSize: 15, textAlign: "center" },
  clearFilter:  { marginTop: 12, color: "#080680", fontSize: 14, fontWeight: "600" },

  // Card
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    borderLeftWidth: 4,
    borderLeftColor: "#080680",
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  clientBlock: { flex: 1, gap: 2, marginRight: 8 },
  clientName:  { fontSize: 15, fontWeight: "700", color: "#1a1a1a" },
  clientCuit:  { fontSize: 12, color: "#888" },

  divider: { height: 1, backgroundColor: "#f0f0f0", marginBottom: 10 },

  cardContent: { gap: 8 },
  row: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  dateContainer: { flexDirection: "row", alignItems: "center", gap: 4 },
  dateText:  { fontSize: 12, color: "#888" },
  totalValue:{ fontSize: 15, fontWeight: "700", color: "#080680" },

  tagsRow: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  tag: {
    backgroundColor: "#EEF0FB",
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  tagText:    { fontSize: 11, color: "#080680", fontWeight: "600" },
  tagDrn:     { backgroundColor: "#FFF3E0" },
  tagTextDrn: { color: "#E65100" },
  tagNeutral: { backgroundColor: "#F5F5F5", borderRadius: 6, paddingHorizontal: 7, paddingVertical: 2 },
  tagTextNeutral: { fontSize: 11, color: "#666" },
  tagDiscount: { backgroundColor: "#F1F8E9", borderRadius: 6, paddingHorizontal: 7, paddingVertical: 2 },
  tagTextDiscount: { fontSize: 11, color: "#558B2F", fontWeight: "600" },

  // Status badges
  statusBadge:    { paddingHorizontal: 9, paddingVertical: 3, borderRadius: 20, borderWidth: 1 },
  statusProcesando: { backgroundColor: "#FFF3E0", borderColor: "#FF9800" },
  statusPendiente:  { backgroundColor: "#FFF8E1", borderColor: "#FFC107" },
  statusFinalizado: { backgroundColor: "#E8F5E9", borderColor: "#4CAF50" },
  statusText:            { fontSize: 11, fontWeight: "700" },
  statusTextProcesando:  { color: "#E65100" },
  statusTextPendiente:   { color: "#F57F17" },
  statusTextFinalizado:  { color: "#2E7D32" },
});
